<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import { Activity, AlertTriangle, CheckCircle2, Clock3, KeyRound, RefreshCw, ShieldAlert } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import QuenticTokenDialog from './QuenticTokenDialog.vue'

const { t, locale } = useI18n()
const loading = ref(false)
const errorMessage = ref('')
const dashboard = ref<any>({
  timeZone: 'Europe/Berlin',
  summary: {},
  customers: [],
  requests: { items: [], total: 0, page: 1, perPage: 25, totalPages: 1 },
})
const selectedCustomerId = ref('')
const selectedStatus = ref('')
const tokenDialogOpen = ref(false)
const tokenCustomer = ref<any>(null)

const summaryCards = computed(() => [
  {
    label: t('view.quentic_dashboard_success_24h'),
    value: dashboard.value.summary?.successfulLast24Hours ?? 0,
    icon: CheckCircle2,
    iconClass: 'text-green-600',
  },
  {
    label: t('view.quentic_dashboard_success_7d'),
    value: dashboard.value.summary?.successfulLast7Days ?? 0,
    icon: Activity,
    iconClass: 'text-blue-600',
  },
  {
    label: t('view.quentic_dashboard_unauthorized_24h'),
    value: dashboard.value.summary?.unauthorizedLast24Hours ?? 0,
    icon: ShieldAlert,
    iconClass: 'text-red-600',
  },
  {
    label: t('view.quentic_dashboard_unauthorized_7d'),
    value: dashboard.value.summary?.unauthorizedLast7Days ?? 0,
    icon: ShieldAlert,
    iconClass: 'text-red-500',
  },
  {
    label: t('view.quentic_dashboard_tokens_due'),
    value: `${dashboard.value.summary?.tokensDueForRotation ?? 0} / ${dashboard.value.summary?.customersWithToken ?? 0}`,
    icon: Clock3,
    iconClass: 'text-amber-600',
  },
  {
    label: t('view.quentic_dashboard_last_success'),
    value: formatDate(dashboard.value.summary?.lastSuccessfulRequest?.requestedAt),
    icon: Clock3,
    iconClass: 'text-slate-600',
  },
])

async function loadDashboard(page = 1) {
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await axios.get('/quentic_dashboard', {
      params: {
        page,
        perPage: 25,
        customerId: selectedCustomerId.value || undefined,
        status: selectedStatus.value || undefined,
      },
    })
    dashboard.value = response.data
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.error ||
      error.response?.statusText ||
      t('view.quentic_dashboard_load_failed')
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  loadDashboard(1)
}

function openTokenDialog(customer: any) {
  tokenCustomer.value = customer
  tokenDialogOpen.value = true
}

function tokenRotated(event: { customerId: string; generatedAt: string | null }) {
  if (tokenCustomer.value) {
    tokenCustomer.value.hasQuenticToken = true
    tokenCustomer.value.tokenGeneratedAt = event.generatedAt
    tokenCustomer.value.rotationDue = false
    tokenCustomer.value.tokenAgeDays = 0
  }
  loadDashboard(dashboard.value.requests?.page || 1)
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: dashboard.value.timeZone || 'Europe/Berlin',
    timeZoneName: 'short',
  }).format(new Date(value))
}

function formatBytes(value: number | null | undefined) {
  if (value === null || value === undefined) return '—'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KiB`
  return `${(value / (1024 * 1024)).toFixed(2)} MiB`
}

function outcomeLabel(outcome: string) {
  if (outcome === 'success') return t('view.quentic_dashboard_outcome_success')
  if (outcome === 'export_error') return t('view.quentic_dashboard_outcome_error')
  if (outcome?.startsWith('unauthorized')) return t('view.quentic_dashboard_outcome_unauthorized')
  return outcome || '—'
}

function outcomeVariant(status: number) {
  if (status === 200) return 'secondary'
  if (status === 401 || status >= 500) return 'destructive'
  return 'outline'
}

onMounted(() => loadDashboard())
</script>

<template>
  <div class="w-full space-y-6 p-4 md:p-6">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="text-2xl font-semibold tracking-tight">{{ t('view.quentic_dashboard_title') }}</h1>
        <p class="text-sm text-muted-foreground">
          {{ t('view.quentic_dashboard_subtitle', { zone: dashboard.timeZone || 'Europe/Berlin' }) }}
        </p>
      </div>
      <Button variant="outline" :disabled="loading" @click="loadDashboard(dashboard.requests?.page || 1)">
        <RefreshCw :class="['mr-2 h-4 w-4', { 'animate-spin': loading }]" />
        {{ t('view.quentic_dashboard_refresh') }}
      </Button>
    </div>

    <p v-if="errorMessage" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      {{ errorMessage }}
    </p>

    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <Card v-for="card in summaryCards" :key="card.label">
        <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle class="text-sm font-medium">{{ card.label }}</CardTitle>
          <component :is="card.icon" :class="['h-4 w-4', card.iconClass]" />
        </CardHeader>
        <CardContent>
          <div class="text-2xl font-bold">{{ card.value }}</div>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle class="flex items-center gap-2 text-lg">
          <KeyRound class="h-5 w-5" />
          {{ t('view.quentic_dashboard_customer_tokens') }}
        </CardTitle>
      </CardHeader>
      <CardContent class="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{{ t('view.quentic_dashboard_customer') }}</TableHead>
              <TableHead>{{ t('view.quentic_dashboard_token_age') }}</TableHead>
              <TableHead>{{ t('view.quentic_dashboard_last_request') }}</TableHead>
              <TableHead>{{ t('view.quentic_dashboard_last_origin') }}</TableHead>
              <TableHead class="text-right">{{ t('view.quentic_dashboard_requests_90d') }}</TableHead>
              <TableHead class="text-right">{{ t('view.quentic_dashboard_ips_90d') }}</TableHead>
              <TableHead class="text-right">{{ t('view.actions') }}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="customer in dashboard.customers" :key="customer.customerId">
              <TableCell>
                <div class="font-medium">{{ customer.customerName }}</div>
                <div class="text-xs text-muted-foreground">ID {{ customer.customerId }}</div>
              </TableCell>
              <TableCell>
                <Badge :variant="customer.rotationDue ? 'destructive' : 'secondary'">
                  {{ customer.tokenAgeDays === null ? t('view.quentic_dashboard_unknown_age') : t('view.quentic_dashboard_days', { count: customer.tokenAgeDays }) }}
                </Badge>
                <div class="mt-1 text-xs text-muted-foreground">{{ formatDate(customer.tokenGeneratedAt) }}</div>
              </TableCell>
              <TableCell>{{ formatDate(customer.lastSuccessfulRequestAt) }}</TableCell>
              <TableCell class="max-w-xs">
                <div class="font-mono text-xs">{{ customer.lastRemoteIp || '—' }}</div>
                <div class="truncate text-xs text-muted-foreground" :title="customer.lastClient">{{ customer.lastClient || '—' }}</div>
              </TableCell>
              <TableCell class="text-right">{{ customer.successfulRequests90Days }}</TableCell>
              <TableCell class="text-right">{{ customer.distinctSuccessfulIps90Days }}</TableCell>
              <TableCell class="text-right">
                <Button size="sm" variant="outline" @click="openTokenDialog(customer)">
                  <KeyRound class="mr-2 h-4 w-4 text-green-600" />
                  {{ t('view.quentic_token_rotate') }}
                </Button>
              </TableCell>
            </TableRow>
            <TableRow v-if="!dashboard.customers?.length">
              <TableCell colspan="7" class="py-8 text-center text-muted-foreground">
                {{ t('view.quentic_dashboard_no_tokens') }}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    <Card>
      <CardHeader class="space-y-4">
        <CardTitle class="flex items-center gap-2 text-lg">
          <Activity class="h-5 w-5" />
          {{ t('view.quentic_dashboard_recent_requests') }}
        </CardTitle>
        <div class="flex flex-wrap gap-3">
          <select v-model="selectedCustomerId" class="h-9 min-w-56 rounded-md border bg-background px-3 text-sm" @change="applyFilters">
            <option value="">{{ t('view.quentic_dashboard_all_customers') }}</option>
            <option v-for="customer in dashboard.customers" :key="customer.customerId" :value="customer.customerId">
              {{ customer.customerName }} ({{ customer.customerId }})
            </option>
          </select>
          <select v-model="selectedStatus" class="h-9 min-w-48 rounded-md border bg-background px-3 text-sm" @change="applyFilters">
            <option value="">{{ t('view.quentic_dashboard_all_statuses') }}</option>
            <option value="success">{{ t('view.quentic_dashboard_outcome_success') }}</option>
            <option value="unauthorized">{{ t('view.quentic_dashboard_outcome_unauthorized') }}</option>
            <option value="error">{{ t('view.quentic_dashboard_outcome_error') }}</option>
          </select>
        </div>
      </CardHeader>
      <CardContent class="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{{ t('view.quentic_dashboard_time') }}</TableHead>
              <TableHead>{{ t('view.quentic_dashboard_customer') }}</TableHead>
              <TableHead>{{ t('view.quentic_dashboard_status') }}</TableHead>
              <TableHead>{{ t('view.quentic_dashboard_origin') }}</TableHead>
              <TableHead class="text-right">{{ t('view.quentic_dashboard_duration') }}</TableHead>
              <TableHead class="text-right">{{ t('view.quentic_dashboard_size') }}</TableHead>
              <TableHead class="text-right">{{ t('view.quentic_dashboard_records') }}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="request in dashboard.requests?.items" :key="request.id">
              <TableCell class="whitespace-nowrap">{{ formatDate(request.requestedAt) }}</TableCell>
              <TableCell>
                <template v-if="request.customerId">
                  <div class="font-medium">{{ request.customerName }}</div>
                  <div class="text-xs text-muted-foreground">ID {{ request.customerId }}</div>
                </template>
                <span v-else class="text-muted-foreground">{{ t('view.quentic_dashboard_unattributed') }}</span>
              </TableCell>
              <TableCell>
                <Badge :variant="outcomeVariant(request.status)">{{ request.status }} · {{ outcomeLabel(request.outcome) }}</Badge>
              </TableCell>
              <TableCell class="max-w-sm">
                <div class="font-mono text-xs">{{ request.remoteIp || '—' }}</div>
                <div class="truncate text-xs text-muted-foreground" :title="request.client">{{ request.client || '—' }}</div>
              </TableCell>
              <TableCell class="text-right">{{ request.durationMs === null ? '—' : `${request.durationMs} ms` }}</TableCell>
              <TableCell class="text-right">{{ formatBytes(request.responseBytes) }}</TableCell>
              <TableCell class="text-right">
                <span v-if="request.rechtsnormenCount !== null">{{ request.rechtsnormenCount }} / {{ request.rechtspflichtenCount }}</span>
                <span v-else>—</span>
              </TableCell>
            </TableRow>
            <TableRow v-if="!dashboard.requests?.items?.length">
              <TableCell colspan="7" class="py-8 text-center text-muted-foreground">
                {{ t('view.quentic_dashboard_no_requests') }}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <div class="mt-4 flex items-center justify-between gap-3">
          <p class="text-sm text-muted-foreground">
            {{ t('view.quentic_dashboard_request_total', { count: dashboard.requests?.total || 0 }) }}
          </p>
          <div class="flex items-center gap-2">
            <Button variant="outline" size="sm" :disabled="loading || dashboard.requests?.page <= 1" @click="loadDashboard(dashboard.requests.page - 1)">
              {{ t('view.prev') }}
            </Button>
            <span class="text-sm">{{ dashboard.requests?.page || 1 }} / {{ dashboard.requests?.totalPages || 1 }}</span>
            <Button variant="outline" size="sm" :disabled="loading || dashboard.requests?.page >= dashboard.requests?.totalPages" @click="loadDashboard(dashboard.requests.page + 1)">
              {{ t('view.next') }}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    <div class="flex items-center gap-2 text-xs text-muted-foreground">
      <AlertTriangle class="h-4 w-4" />
      {{ t('view.quentic_dashboard_privacy_note') }}
    </div>

    <QuenticTokenDialog
      v-model:open="tokenDialogOpen"
      :customer="tokenCustomer"
      @rotated="tokenRotated"
    />
  </div>
</template>
